import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { anzahlSpaltenumbrueche, readBodyOoxml } from "@/office/word/einfuegen";
import { readLayout } from "@/office/word/layout";
import { MAKRO_DOKUMENT } from "../makros";
import { pruefeSeite } from "./seite";

export const lektionSpalten: Lesson = {
  id: "spalten",
  title: "Text in Spalten",
  summary: "Zeitungsspalten für einen Teil des Dokuments – mit Abschnittswechsel und Spaltenumbruch.",
  durationMinutes: 9,
  learningGoals: [
    "Text über Layout → Spalten in zwei oder drei Spalten setzen",
    "Spalten mit einem fortlaufenden Abschnittswechsel auf einen Teil begrenzen",
    "Einen Spaltenumbruch statt Leerzeilen verwenden",
  ],
  steps: [
    {
      id: "zwei-spalten",
      title: "Zwei Spalten für einen Teil des Dokuments",
      explanation: [
        "„Layout“ → „Spalten“ setzt Text in Zeitungsspalten. Steht der Cursor nur irgendwo " +
          "im Text, gilt die Einstellung für den ganzen Abschnitt – meist also für das ganze " +
          "Dokument.",
        "Sollen nur einige Absätze zweispaltig sein, markierst du sie vorher. Word setzt " +
          "dann selbst einen fortlaufenden Abschnittswechsel davor und dahinter. Ein " +
          "fortlaufender Abschnittswechsel beginnt keine neue Seite, erlaubt aber ein " +
          "anderes Seitenlayout – genau das, was Spalten brauchen (siehe Modul " +
          "„Seitenlayout“).",
        "Unter „Weitere Spalten …“ stehen Spaltenbreite, Abstand und die Option " +
          "„Zwischenlinie“.",
      ],
      keyPoints: [
        "Layout → Spalten.",
        "Vorher markieren = Spalten nur für diesen Teil.",
        "Word setzt fortlaufende Abschnittswechsel selbst.",
      ],
      task: {
        instruction:
          "Setze einen Teil deines Dokuments zweispaltig, während der Rest (etwa die " +
          "Überschrift) einspaltig bleibt.",
        hint: "Nur die Absätze markieren, die zweispaltig werden sollen, dann Layout → Spalten → Zwei.",
        macro: MAKRO_DOKUMENT,
        macroNurErsatz: true,
        check: async () => {
          const layout = await readLayout();
          return pruefeSeite(({ spaltenMax }) =>
            fromCriteria(
              [
                criterion(`Ein Abschnitt hat mindestens zwei Spalten (höchstens gefunden: ${spaltenMax})`, spaltenMax >= 2),
                criterion(
                  `Das Dokument hat mehrere Abschnitte (gefunden: ${layout.sections.length})`,
                  layout.sections.length >= 2,
                ),
              ],
              {
                success: "Zweispaltig, wo es passt – der Rest bleibt, wie er war.",
                failure: "Die Spalten gelten noch nicht für einen eigenen Abschnitt.",
                hint: "Wenn das ganze Dokument zweispaltig ist: rückgängig machen, zuerst markieren, dann Spalten wählen.",
              },
            ),
          );
        },
      },
    },
    {
      id: "spaltenumbruch",
      title: "Der Spaltenumbruch",
      explanation: [
        "Soll ein Absatz oben in der nächsten Spalte beginnen, drücken viele so lange " +
          "Enter, bis er dort landet. Das ist die Leerzeile aus dem Modul „Absätze“ in " +
          "neuer Gestalt.",
        "„Layout“ → „Umbrüche“ → „Spalte“ (Strg + Umschalt + Enter) erzwingt den Wechsel " +
          "in die nächste Spalte, egal wie sich der Text davor verändert.",
      ],
      keyPoints: [
        "Layout → Umbrüche → Spalte.",
        "Strg + Umschalt + Enter.",
      ],
      task: {
        instruction: "Füge in deinem zweispaltigen Teil einen Spaltenumbruch ein.",
        hint: "Cursor an den Anfang des Absatzes, der oben in der rechten Spalte stehen soll, dann Strg + Umschalt + Enter.",
        check: async () => {
          const umbrueche = anzahlSpaltenumbrueche(await readBodyOoxml());
          return fromCriteria([criterion(`Spaltenumbrüche im Dokument: ${umbrueche}`, umbrueche > 0)], {
            success: "Der Umbruch hält die Spalte, auch wenn sich der Text davor ändert.",
            failure: "Es gibt noch keinen Spaltenumbruch.",
            hint: "Ein Seitenumbruch (Strg + Enter) ist etwas anderes – hier Strg + Umschalt + Enter.",
          });
        },
      },
    },
  ],
};
